import React, { createRef, useContext, useEffect, useLayoutEffect } from 'react';
import { Box, Optional, Row } from '@honda-canada/design-system-react';
import { useLocation } from 'react-router-dom';

import HeaderContext from '../service/HeaderContext';
import MainMenuItems from './MainMenuItems';
import isSSR from '../../../utils/isSSR';
import themeStyles, { HeaderBarButtonPadding } from '../styles/Header.styles';
import { primaryNavItemDisplayTarget } from '../service/utils';

const NavContainer = themeStyles.apply(Row, 'NavContainer');
const NavAnimation = themeStyles.apply(Box, 'NavAnimation');

const MenuMain = () => {
  const location = useLocation();
  const {
    activeMenu,
    handleHeaderMegaMenuScroll,
    handleHeaderNavScroll,
    headerBarMenuAnimationProps,
    leftPrimaryItems,
    megaMenuRef,
    navItemUnderline,
    primaryItems,
    primaryItemsRef,
    rightPrimaryItems,
    selectedMenu,
    setAnimationProps,
    setSelectedMenu,
    styles,
    getPrimaryNavItemsToRender,
  } = useContext(HeaderContext);

  useEffect(() => {
    setSelectedMenu(location.state?.selectedMenuItem);
  }, [location]);

  useLayoutEffect(() => {
    if (isSSR()) return undefined;
    if (activeMenu) {
      handleHeaderNavScroll();
      handleHeaderMegaMenuScroll();
    }
    window.addEventListener('scroll', handleHeaderNavScroll);
    window.addEventListener('scroll', handleHeaderMegaMenuScroll);

    return () => {
      window.removeEventListener('scroll', handleHeaderNavScroll);
      window.removeEventListener('scroll', handleHeaderMegaMenuScroll);
    };
  }, [activeMenu]);

  useEffect(() => {
    async function checkFontStatus() {
      if ('fonts' in document) {
        await document.fonts.ready;
        const activeMenuRef = primaryItemsRef.current?.[selectedMenu]?.current;

        if (!activeMenuRef) {
          setAnimationProps({});
          return;
        }

        const parentRef = activeMenuRef.parentNode;

        const activeOffsetWidth = navItemUnderline
          ? activeMenuRef.offsetWidth + 2 * HeaderBarButtonPadding
          : activeMenuRef.offsetWidth;
        const width = activeMenuRef ? activeOffsetWidth - 2 * HeaderBarButtonPadding : 0;
        const left = parentRef ? parentRef.offsetLeft + HeaderBarButtonPadding : 0;

        setAnimationProps({ width, left });
      }
    }
    checkFontStatus();
  }, [selectedMenu]);

  if (!Array.isArray(primaryItems) || !primaryItems?.length) {
    return null;
  }

  // Get primary nav items that should be rendered on desktop
  const [primaryItemsToRender, leftPrimaryItemsToRender, rightPrimaryItemsToRender] = [
    primaryItems,
    leftPrimaryItems,
    rightPrimaryItems,
  ].map(arr => getPrimaryNavItemsToRender(arr, primaryNavItemDisplayTarget.desktop));

  primaryItemsRef.current = primaryItemsToRender?.reduce(
    (acc, { itemDisplayName }) => ({
      ...acc,
      [itemDisplayName]: primaryItemsRef.current[itemDisplayName] ?? createRef(),
    }),
    {},
  );

  megaMenuRef.current = primaryItemsToRender?.reduce(
    (acc, { itemDisplayName }) => ({
      ...acc,
      [itemDisplayName]: megaMenuRef.current[itemDisplayName] ?? createRef(),
    }),
    {},
  );

  return (
    <NavContainer position="relative">
      <MainMenuItems renderItems={leftPrimaryItemsToRender} />
      <MainMenuItems renderItems={rightPrimaryItemsToRender} />
      <Optional when={activeMenu}>
        <NavAnimation
          {...headerBarMenuAnimationProps}
          {...styles.navAnimationItem}
          navItemUnderline={navItemUnderline}
          data-testid="header-menu-nav-animation"
        />
      </Optional>
    </NavContainer>
  );
};

export default MenuMain;
